import { useState } from 'react';
import { Link } from 'react-router';
import {
  BulleIcone, Carte, EnTetePage, ICONES_MODULE,
  IconeCalendrier, IconeChevronDroite, IconeEmploiDuTemps, IconeEquipe, IconePlus,
  Squelette, Vide,
} from '@journal/ui';
import { LienBouton } from '../components/LienBouton.tsx';
import { PenseBete } from '../components/PenseBete.tsx';
import { PARAMETRE_CLASSE } from './Evaluation.tsx';
import { classeChoisie } from '../components/SelecteurClasse.tsx';
import { PremierParametrage } from '../components/PremierParametrage.tsx';
import { useDonnees } from '../lib/donnees.ts';
import { useAnneeActive } from '../lib/annee.tsx';
import { useAbonnement, useFonctionnalite } from '../lib/droits.tsx';
import { aujourdhuiIso, dateLongue, type LigneJour } from './Journal.tsx';

/**
 * Accueil.
 *
 * ── Un point de départ, pas un tableau de bord ──────────────────────────────
 * L'écran a longtemps porté la journée du jour et quatre indicateurs. La
 * journée recopiait le cahier journal, à un clic de là ; les indicateurs
 * rassuraient sans servir à travailler — on ne prépare pas sa semaine en
 * lisant « 10 élèves ». Les deux sont partis.
 *
 * Reste ce dont on se sert : la date, ce qui appelle une décision — un essai
 * qui court, une équipe à inviter, une année à créer — et l'accès aux modules.
 *
 * ── Pourquoi il ne remplace pas le cahier journal ───────────────────────────
 * On n'y saisit rien. Un écran qui affiche ET permet de modifier duplique la
 * logique du cahier journal, et les deux divergent au premier changement. Ici,
 * tout mène au journal.
 */

/*
 * L'ordre est celui de la barre latérale — journée, puis préparation, puis
 * l'annexe, puis les réglages. Deux listes qui rangent les mêmes modules
 * autrement obligent à réapprendre l'écran à chaque fois qu'on change de
 * chemin.
 *
 * « Élèves » menait à `/eleves`, une adresse qui n'existe plus : la liste de
 * classe se gère depuis Évaluation, et le lien ramenait à l'accueil sans rien
 * dire. Il ouvre désormais Évaluation avec la gestion de la classe déjà
 * ouverte — c'est ce qu'on venait chercher en cliquant.
 */
const RACCOURCIS = [
  { chemin: '/journal', id: 'journal', titre: 'Cahier journal', description: 'Préparer et consigner les séances' },
  { chemin: '/fiches-preparation', id: 'fiches', titre: 'Fiche de préparation', description: 'Séquences, séances et phases' },
  { chemin: '/emploi-du-temps', id: 'edt', titre: 'Emploi du temps', description: 'Semaine type' },
  { chemin: '/progressions', id: 'progressions', titre: 'Progressions', description: 'Prévoir la semaine, reporter' },
  { chemin: '/programmation', id: 'programmation', titre: 'Programmation', description: 'Répartir le programme sur l’année' },
  { chemin: '/evaluation', id: 'evaluation', titre: 'Évaluation', description: 'Compétences et synthèse' },
  { chemin: `/evaluation?${PARAMETRE_CLASSE}=ouverte`, id: 'eleves', titre: 'Élèves', description: 'Liste de classe et import' },
  { chemin: '/agenda', id: 'agenda', titre: 'Agenda', description: 'Une page par jour, en écriture libre' },
  { chemin: '/heures', id: 'heures', titre: '108 heures', description: 'Suivi des heures de service' },
  { chemin: '/impressions', id: 'impressions', titre: 'Impressions', description: 'Le dossier de la classe, en un document' },
  { chemin: '/reglages', id: 'reglages', titre: 'Réglages', description: 'Année, classe, matières' },
] as const;

interface VueJour {
  date: string;
  rows: LigneJour[];
}

/**
 * Ce qui est écrit pour une ligne, en une phrase.
 *
 * Les OBJECTIFS de la séance, première ligne de chacun : c'est ce que le
 * cahier journal fait saisir, comme l'original. La séquence et l'objectif
 * « global » n'y ont plus de case — les afficher ici montrerait un texte
 * qu'on ne retrouve nulle part en ouvrant le journal.
 */
export function intitule(ligne: LigneJour): string {
  const l = ligne.lesson;
  if (!l) return '';
  return l.objectives
    .map((o) => o.text.split(String.fromCharCode(10))[0]!.trim())
    .filter(Boolean)
    .join(' · ');
}

export function Accueil() {
  const annee = useAnneeActive();
  /*
   * La boîte se referme dès l'année ouverte. On garde l'écart en mémoire le
   * temps que `useAnneeActive` se recharge : sans lui, elle clignoterait une
   * fois de plus entre la réponse du serveur et la mise à jour de l'état.
   */
  const [parametrageEcarte, setParametrageEcarte] = useState(false);
  const date = aujourdhuiIso();

  const jour = useDonnees<VueJour>(`/lessons/day?date=${date}`, [date]);

  // Droits déjà chargés par la coquille : la carte d'amorce ne coûte aucune
  // requête de plus.
  const equipeIncluse = useFonctionnalite('equipe');
  // Même règle que la barre latérale : une classe dont on est titulaire, ou
  // une classe qu'on a ouverte — ce que fait un directeur qui va voir le
  // cahier journal de son école.
  const mesClasses = useDonnees<Array<{ id: string; teacherId: string | null }>>('/classrooms');
  const moi = useDonnees<{
    id: string; role: 'OWNER' | 'ADMIN' | 'MEMBER'; impersonation?: boolean;
  }>('/auth/me');
  const choisie = classeChoisie();
  const aUneClasse = !mesClasses.donnees || !moi.donnees
    || mesClasses.donnees.some((c) => c.teacherId === moi.donnees!.id)
    || mesClasses.donnees.some((c) => c.id === choisie)
    // Le SUPPORT n'est titulaire d'aucune classe : sans cela, il voyait
    // l'écran de direction au lieu de l'espace qu'il vient consulter.
    || (moi.donnees.impersonation === true && mesClasses.donnees.length > 0);
  const abonnement = useAbonnement();
  const comptesOuverts = abonnement.donnees?.quotas
    .find((q) => q.metric === 'users')?.used ?? 0;

  /*
   * Compte de direction : l'accueil EST l'administration.
   *
   * « Aujourd'hui » est un écran d'enseignant — la journée, les séances à
   * préparer. Un compte qui administre les comptes et répartit les classes n'y
   * a rien à lire, et le serveur ne lui reconnaît aucune classe de travail :
   * la page s'ouvrirait vide, sans rien expliquer.
   *
   * Il atterrit donc directement sur ce qu'il vient faire.
   */
  /*
   * ── L'année d'ABORD, avant même l'écran d'administration ─────────────────
   *
   * Une école neuve n'a aucune classe : son compte de direction tombait donc
   * sur « Administration » et ne voyait jamais le paramétrage — précisément
   * dans le cas pour lequel il existe. L'année est le préalable de l'école
   * entière ; la classe ne concerne qu'un enseignant.
   */
  if (annee.erreur && !parametrageEcarte) {
    return (
      <PremierParametrage
        peutAdministrer={moi.donnees?.role === 'OWNER' || moi.donnees?.role === 'ADMIN'}
        // « equipe » n'est servie qu'aux espaces de profil ÉCOLE, en essai
        // comme sur une offre payante : c'est le signal le plus sûr.
        estEcole={equipeIncluse}
        surTermine={() => { setParametrageEcarte(true); annee.recharger(); }}
      />
    );
  }

  if (!aUneClasse) {
    return (
      <>
        <EnTetePage titre="Administration" />
        <div className="flex flex-col gap-5">
          <Carte
            titre="Votre école"
            description="Votre compte administre les comptes et les classes. Il ne tient pas de cahier journal."
          >
            <p className="text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
              Créez les comptes de vos enseignants, créez les classes et
              confiez-les. Chaque enseignant retrouve alors sa classe, ses
              élèves et son journal, sans voir ceux des autres.
            </p>
            <p className="mt-4">
              <LienBouton vers="/equipe" icone={<IconeEquipe taille={16} />}>
                Comptes et classes
              </LienBouton>
            </p>
          </Carte>

          {/*
            * L'issue vers l'enseignement reste dite, sans être un bouton :
            * beaucoup de directeurs d'école primaire ont une classe, et
            * découvrir par hasard qu'il suffit de s'en affecter une serait
            * une perte de temps évitable.
            */}
          <Carte titre="Vous avez aussi une classe ?">
            <p className="text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
              Confiez-vous une classe depuis l’écran Comptes et classes : le
              cahier journal, l’emploi du temps et les progressions
              réapparaîtront dans le menu.
            </p>
          </Carte>
        </div>
      </>
    );
  }

  /*
   * ── Le paramétrage minimal, demandé tout de suite ────────────────────────
   *
   * Sans année active, la moitié de l'application répond en erreur. L'écran
   * ci-dessous le disait déjà et renvoyait vers les Réglages — mais il taisait
   * la ZONE de vacances, dont dépendent les dates des périodes : une année
   * créée sans elle naît sans découpage, et rien ne l'explique.
   *
   * La boîte pose les deux questions ensemble, dans l'ordre qui convient. Elle
   * ne s'ouvre que tant que la condition tient ; l'écran qui suit reste pour
   * qui la referme.
   */
  if (annee.erreur) {
    return (
      <>
        <EnTetePage titre="Bienvenue" />
        <Carte>
          <Vide
            icone={<IconeCalendrier taille={28} />}
            titre="Aucune année scolaire active"
            // L'issue est un bouton, pas un lien souligné perdu dans le
            // paragraphe : c'est la seule chose à faire depuis cet écran, et
            // rien d'autre ne fonctionnera tant qu'elle ne l'est pas.
            action={(
              <LienBouton vers="/reglages" icone={<IconePlus taille={16} />}>
                Créer une année scolaire
              </LienBouton>
            )}
          >
            Tout (élèves, séances, progressions) se rattache à une année
            scolaire.
          </Vide>
        </Carte>
      </>
    );
  }

  const lignes = jour.donnees?.rows ?? [];

  /** Le matériel écrit pour aujourd'hui, une entrée par créneau qui en porte. */
  const materiel = lignes
    .map((l, i) => ({
      cle: l.slotId ?? `${l.startsAt}-${i}`,
      matiere: l.subjectName || 'Sans matière',
      texte: (l.lesson?.materials ?? '').trim(),
    }))
    .filter((m) => m.texte !== '');

  return (
    <div className="flex flex-col gap-5">
      <EnTetePage
        titre="Aujourd'hui"
        contexte={`${dateLongue(date)}${annee.donnees ? ` · ${annee.donnees.label}` : ''}`}
      />

      {/*
        * Amorce pour une école encore seule.
        *
        * Un directeur qui vient de créer l'espace de son école y est seul :
        * l'écran Équipe existe, mais rien ne l'y mène tant qu'il ne pense pas à
        * le chercher dans la barre latérale. La carte disparaît dès qu'un
        * deuxième compte existe — une invitation permanente deviendrait un
        * meuble qu'on ne lit plus.
        *
        * Le compte d'utilisateurs vient du quota déjà chargé par la coquille :
        * aucune requête supplémentaire pour cette carte.
        */}
      {equipeIncluse && comptesOuverts === 1 && (
        <Carte titre="Invitez votre équipe">
          <p className="text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
            Vous êtes seul dans cet espace. Chaque enseignant invité obtient son
            propre accès, avec ses classes et ses séances ; vous gardez la main
            sur les comptes.
          </p>
          <p className="mt-4">
            <LienBouton vers="/equipe" icone={<IconeEquipe taille={16} />}>
              Gérer les comptes
            </LienBouton>
          </p>
        </Carte>
      )}

      {/*
        * ── Ce qui est PRÉPARÉ pour aujourd'hui ──────────────────────────────
        *
        * Pas la liste des créneaux avec leur état : cet écran-là recopiait le
        * cahier journal et n'apprenait rien qu'un clic ne montre. Ce qu'on
        * vient chercher ici, c'est ce qu'on a écrit — la séquence, l'objectif —
        * et ce qu'il faut avoir sorti avant que la classe entre.
        *
        * Lecture seule, et c'est délibéré : un écran qui affiche ET modifie
        * duplique la logique du journal, et les deux divergent au premier
        * changement.
        */}
      <Carte
        titre="Aujourd'hui"
        action={
          <LienBouton vers={`/journal?date=${date}`} variante="secondary" taille="sm">
            Ouvrir le cahier journal
          </LienBouton>
        }
      >
        {jour.chargement && !jour.donnees ? (
          <div className="flex flex-col gap-1.5">
            <Squelette className="h-14" />
            <Squelette className="h-14" />
            <Squelette className="h-14" />
          </div>
        ) : lignes.length === 0 ? (
          <Vide icone={<IconeEmploiDuTemps taille={28} />} titre="Pas de classe aujourd'hui">
            Aucun créneau n'est prévu ce jour dans votre semaine type.
          </Vide>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {lignes.map((l, i) => {
              const texte = intitule(l);
              return (
                <li
                  key={l.slotId ?? `${l.startsAt}-${i}`}
                  // Bordure gauche à la couleur de la matière ; le nom reste
                  // écrit à côté — la couleur ne porte jamais l'information
                  // seule.
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-[var(--color-border)] border-l-[3px] bg-[var(--color-surface)] px-3 py-2.5 text-sm"
                  style={{ borderLeftColor: l.color }}
                >
                  <span className="w-[6.5rem] shrink-0 tabular-nums text-[var(--color-fg-muted)]">
                    {l.startsAt ? `${l.startsAt}–${l.endsAt ?? ''}` : '—'}
                  </span>
                  <span className="font-medium">{l.subjectName || 'Sans matière'}</span>
                  <span
                    className={[
                      'min-w-0 flex-1 basis-full sm:basis-auto',
                      texte ? 'text-[var(--color-fg-muted)]' : 'italic text-[var(--color-fg-subtle)]',
                    ].join(' ')}
                  >
                    {/* Dire le vide plutôt que le laisser : une ligne nue se lit
                        comme un défaut d'affichage. */}
                    {texte || 'Rien de préparé'}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/*
          * Le matériel, RASSEMBLÉ pour la journée.
          *
          * Il figure déjà séance par séance dans le journal, ce qui répond à
          * « de quoi ai-je besoin à 10 h ». La question posée la veille au soir
          * est autre : « qu'est-ce que je sors avant de partir ». Elle demande
          * une seule liste, et c'est le seul endroit où elle existe.
          *
          * Le texte est repris tel quel, sans le découper : un enseignant y
          * écrit aussi bien « ardoises » qu'une phrase entière, et deviner un
          * séparateur reviendrait à couper ses phrases au hasard.
          */}
        {materiel.length > 0 && (
          <div className="mt-4 border-t border-[var(--color-border)] pt-3">
            <h3 className="mb-2 text-[13px] font-semibold">À sortir pour la journée</h3>
            <ul className="flex flex-col gap-1">
              {materiel.map((m) => (
                <li key={m.cle} className="flex gap-2 text-[13px]">
                  <span className="shrink-0 font-medium">{m.matiere}</span>
                  <span className="min-w-0 whitespace-pre-wrap text-[var(--color-fg-muted)]">
                    {m.texte}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Carte>

      {/*
        * ── Le pense-bête EN DESSOUS, sur toute la largeur ───────────────────
        * Il a d'abord été posé à côté, en colonne étroite. Deux effets : le
        * récapitulatif du jour, qui porte des intitulés de séance entiers,
        * perdait un tiers de sa largeur et passait à la ligne tous les trois
        * mots ; et la liste elle-même, dans sa colonne, coupait les tâches un peu
        * longues.
        *
        * Chacun sur toute la largeur, l'un sous l'autre : le jour se lit d'un
        * trait, et la liste vient juste après, à portée de regard.
        */}
      <PenseBete />

      <section>
        <h2 className="mb-3">Accès rapides</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {RACCOURCIS.map((r) => {
            const Icone = ICONES_MODULE[r.id];
            return (
              <Link
                key={r.chemin}
                to={r.chemin}
                className={[
                  'group flex items-start gap-3 rounded-xl border p-4',
                  'border-[var(--color-border)] bg-[var(--color-surface)]',
                  'shadow-[var(--ombre-carte)]',
                  'transition-[background-color,border-color,box-shadow] duration-150',
                  'hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2',
                  'focus-visible:outline-[var(--color-focus)]',
                ].join(' ')}
              >
                <BulleIcone>{Icone && <Icone taille={18} />}</BulleIcone>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-[var(--color-fg)]">{r.titre}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-[var(--color-fg-muted)]">
                    {r.description}
                  </span>
                </span>
                {/* Le chevron n'apparaît qu'au survol : présent en permanence,
                    six fois sur la grille, il ferait du bruit. */}
                <IconeChevronDroite
                  taille={16}
                  className="mt-1 shrink-0 text-[var(--color-fg-muted)] opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                />
              </Link>
            );
          })}
        </div>
      </section>

    </div>
  );
}
